import { useCallback, useState, type ComponentType, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Pin, PinOff } from 'lucide-react';
import type { RailTileColor } from '@clarix/shared-types';
import { Popover, PopoverContent, PopoverTrigger, cn } from '@clarix/ui';
import { useSidebarRailPins } from './hooks/useSidebarRailPins';
import { useSidebar } from './context/SidebarContext';

export interface SidebarMoreItem {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Navigation target. Omit when the item is action-based (see `onSelect`). */
  href?: string;
  openInNewTab?: boolean;
  isVisible: boolean;
  /** If true, hide the pin toggle for this item (e.g., for action-only entries). */
  pinnable?: boolean;
  /** Optional action fired instead of navigation when the row is clicked. */
  onSelect?: () => void;
  /** Tile color for the grid view. Defaults to gray. */
  tileColor?: RailTileColor;
  /** When set, the row opens this sidebar panel in addition to navigating. */
  panelId?: string;
}

interface SidebarMorePopoverProps {
  items: SidebarMoreItem[];
  trigger: ReactNode;
}

const TILE_COLOR_CLASSES: Record<RailTileColor, { bg: string; icon: string }> = {
  green: { bg: 'bg-emerald-50', icon: 'text-emerald-600' },
  blue: { bg: 'bg-blue-50', icon: 'text-blue-600' },
  red: { bg: 'bg-rose-50', icon: 'text-rose-600' },
  amber: { bg: 'bg-amber-50', icon: 'text-amber-600' },
  purple: { bg: 'bg-violet-50', icon: 'text-violet-600' },
  gray: { bg: 'bg-gray-100', icon: 'text-gray-600' },
};

/**
 * More (9-dot) popover — exact port of clario-frontend's SidebarMorePopover:
 * a 3-column grid of colored tiles with a hover pin toggle, plus an optional
 * footer of action-only (non-pinnable) rows.
 */
export function SidebarMorePopover({ items, trigger }: SidebarMorePopoverProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isPinned, togglePin } = useSidebarRailPins();
  const { togglePanel, isPanelOpen, activePanel } = useSidebar();

  const handleRowClick = useCallback(
    (item: SidebarMoreItem) => {
      setOpen(false);
      if (item.onSelect) {
        item.onSelect();
        return;
      }
      if (item.panelId) {
        const alreadyOpen = isPanelOpen && activePanel === item.panelId;
        if (!alreadyOpen) togglePanel(item.panelId);
        if (item.href && !item.openInNewTab) navigate(item.href);
        return;
      }
      if (!item.href) return;
      if (item.openInNewTab) {
        window.open(item.href, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.href);
      }
    },
    [navigate, togglePanel, isPanelOpen, activePanel],
  );

  const visibleItems = items.filter((i) => i.isVisible);
  const pinnableItems = visibleItems.filter((i) => i.pinnable !== false);
  const actionItems = visibleItems.filter((i) => i.pinnable === false);
  // Action-only entries (e.g. "Customize navigation") normally sit in a footer
  // below the pinnable tile grid. When there are no pinnable tiles, promote them
  // into the grid so the popover still shows a proper icon tile instead of an
  // empty grid with a lone footer button.
  const gridItems = pinnableItems.length > 0 ? pinnableItems : actionItems;
  const footerItems = pinnableItems.length > 0 ? actionItems : [];

  if (visibleItems.length === 0) return null;

  // Fit the grid to its contents: 1–2 tiles use 1–2 columns (no dead space on
  // the right), 3+ tiles keep the 3-column layout.
  const gridColsClass =
    gridItems.length <= 1 ? 'grid-cols-1' : gridItems.length === 2 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={12}
        className="bg-surface w-fit rounded-xl p-3 shadow-overlay"
      >
        <div className={cn('grid gap-2', gridColsClass)}>
          {gridItems.map((item) => {
            const Icon = item.icon;
            const pinnedActive = isPinned(item.id);
            const routeActive = item.href ? location.pathname.startsWith(item.href) : false;
            const colors = TILE_COLOR_CLASSES[item.tileColor ?? 'gray'];

            return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                className={cn(
                  'group relative flex min-w-[4.5rem] cursor-pointer flex-col items-center gap-1.5 rounded-lg p-2 transition-colors',
                  'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-200',
                  routeActive && 'bg-indigo-50',
                )}
                onClick={() => handleRowClick(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(item);
                  }
                }}
              >
                <div
                  className={cn('flex h-8 w-8 items-center justify-center rounded-xl', colors.bg)}
                >
                  <Icon className={cn('h-4 w-4', colors.icon)} strokeWidth={1.75} />
                </div>
                <span
                  className={cn(
                    'w-full truncate text-center text-md font-medium leading-tight',
                    routeActive ? 'text-indigo-700' : 'text-gray-700',
                  )}
                >
                  {item.label}
                </span>

                {item.pinnable !== false && (
                  <button
                    type="button"
                    aria-label={pinnedActive ? `Unpin ${item.label}` : `Pin ${item.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(item.id);
                    }}
                    className={cn(
                      'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-opacity',
                      pinnedActive
                        ? 'text-indigo-600 opacity-100'
                        : 'text-gray-500 opacity-0 hover:text-gray-700 group-hover:opacity-100',
                    )}
                  >
                    {pinnedActive ? (
                      <Pin className="h-3 w-3 fill-current" strokeWidth={1.5} />
                    ) : (
                      <PinOff className="h-3 w-3" strokeWidth={1.5} />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {footerItems.length > 0 && (
          <div className="mt-3 flex flex-col gap-1">
            {footerItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleRowClick(item)}
                  className="px-2 flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-gray-200 text-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Icon className="h-4 w-4 text-gray-500" strokeWidth={1.5} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
